import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SignatureList } from './SignatureList';
import { CreateSignatureModal } from './CreateSignatureModal';
import { useSystemSignatures } from '../../../../../hooks/useSystemSignatures';
import type { SystemSignature } from '../../../../../store/reducers/signatures.store';

export const SystemSignatures: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingSignature, setEditingSignature] =
    useState<SystemSignature | null>(null);

  const {
    systemSignatures,
    loading,
    error,
    operationLoading,
    createSystemSignature,
    updateSystemSignature,
    deleteSystemSignature,
    setDefaultSystemSignature,
    clearErrors,
  } = useSystemSignatures();

  const handleCreateSignature = async (signatureData: {
    name: string;
    content: string;
    isDefault: boolean;
    email: string;
    firstName: string;
    lastName: string;
  }) => {
    await createSystemSignature(signatureData);
  };

  const handleUpdateSignature = async (signatureData: {
    name: string;
    content: string;
    isDefault: boolean;
    email: string;
    firstName: string;
    lastName: string;
  }) => {
    if (editingSignature) {
      await updateSystemSignature(editingSignature.id, signatureData);
      setEditingSignature(null);
    }
  };

  const handleEditSignature = (signature: SystemSignature) => {
    setEditingSignature(signature);
    setCreateModalOpen(true);
  };

  const handleDeleteSignature = async (id: string) => {
    await deleteSystemSignature(id);
  };

  const handleSetDefaultSignature = async (id: string) => {
    await setDefaultSystemSignature(id);
  };

  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditingSignature(null);
  };

  const handleModalSave = editingSignature
    ? handleUpdateSignature
    : handleCreateSignature;

  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Team Cowbell Signatures
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage system-wide signatures for Team Cowbell emails
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            disabled={operationLoading}
          >
            Create Team Signature
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Team Signatures:</strong> These signatures are available to
            all users when they select "Team Cowbell" as the sender. The default
            signature is automatically used for auto-generated emails.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => clearErrors()}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <SignatureList
            signatures={systemSignatures}
            onEdit={handleEditSignature}
            onDelete={handleDeleteSignature}
            onSetDefault={handleSetDefaultSignature}
            loading={operationLoading}
            isSystemSignatures={true}
          />
        )}

        <CreateSignatureModal
          open={createModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          editingSignature={editingSignature}
          loading={operationLoading}
          isSystemSignature={true}
        />
      </CardContent>
    </Card>
  );
};
